import { datadogRum } from "@datadog/browser-rum";

export interface DOMErrorContext {
  errorType: "removeChild" | "insertBefore" | "appendChild" | "other";
  url: string;
  pathname: string;
  referrer: string;
  userAgent: string;
  timestamp: string;
  // DOM state
  bodyChildCount: number;
  headChildCount: number;
  // Third-party scripts detected
  gtmLoaded: boolean;
  trustpilotLoaded: boolean;
  // React state
  reactVersion: string;
  // Navigation info
  navigationTiming: {
    loadTime: number;
    domContentLoaded: number;
  } | null;
  // Error stack
  errorStack: string;
  errorMessage: string;
  // Suspected source analysis
  suspectedSource: string;
  stackFrames: string[];
}

/**
 * Classifies the type of DOM error based on the error message
 */
function classifyDOMError(
  message: string
): "removeChild" | "insertBefore" | "appendChild" | "other" {
  if (message.includes("removeChild")) return "removeChild";
  if (message.includes("insertBefore")) return "insertBefore";
  if (message.includes("appendChild")) return "appendChild";
  return "other";
}

/**
 * Checks if a third-party script is loaded
 */
function isScriptLoaded(scriptName: string): boolean {
  if (typeof window === "undefined") return false;

  switch (scriptName) {
    case "gtm":
      return typeof (window as any).dataLayer !== "undefined";
    case "trustpilot":
      return typeof (window as any).Trustpilot !== "undefined";
    default:
      return false;
  }
}

/**
 * Gets navigation timing information
 */
function getNavigationTiming(): { loadTime: number; domContentLoaded: number } | null {
  if (typeof window === "undefined" || !window.performance) return null;

  const timing = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
  if (!timing) return null;

  return {
    loadTime: Math.round(timing.loadEventEnd - timing.startTime),
    domContentLoaded: Math.round(timing.domContentLoadedEventEnd - timing.startTime),
  };
}

/**
 * Analyzes the stack trace to identify the suspected source of the error
 * Returns both the suspected source and the parsed stack frames
 */
function analyzeStackTrace(stack: string): { suspectedSource: string; stackFrames: string[] } {
  if (!stack) return { suspectedSource: "unknown", stackFrames: [] };

  const stackFrames = stack
    .split("\n")
    .slice(1)
    .map((line) => line.trim())
    .filter((line) => line.startsWith("at "))
    .slice(0, 10);

  const patterns = [
    { pattern: /react-svg|ReactSVG/i, source: "react-svg" },
    { pattern: /qr-code-styling|QRCodeStyling/i, source: "qr-code-styling" },
    { pattern: /gtm|google.*tag/i, source: "GTM" },
    { pattern: /trustpilot/i, source: "Trustpilot" },
    { pattern: /datadog/i, source: "Datadog" },
    { pattern: /amplitude/i, source: "Amplitude" },
    { pattern: /chart|doughnut/i, source: "Chart.js" },
    {
      pattern: /react-dom|commitUpdate|commitDeletion|removeChildFromContainer/i,
      source: "React-DOM",
    },
  ];

  const stackString = stack.toLowerCase();

  for (const { pattern, source } of patterns) {
    if (pattern.test(stackString)) {
      return { suspectedSource: source, stackFrames };
    }
  }

  const userFrame = stackFrames.find(
    (frame) =>
      !frame.includes("node_modules") &&
      !frame.includes("react-dom") &&
      !frame.includes("react.") &&
      !frame.includes("scheduler")
  );

  if (userFrame) {
    const match = userFrame.match(/at\s+(\w+)/);
    if (match) {
      return { suspectedSource: `UserCode:${match[1]}`, stackFrames };
    }
  }

  return { suspectedSource: "React-DOM-internal", stackFrames };
}

/**
 * Collects comprehensive context about a DOM manipulation error
 */
export function collectDOMErrorContext(error: Error): DOMErrorContext {
  const { suspectedSource, stackFrames } = analyzeStackTrace(error.stack || "");

  const context: DOMErrorContext = {
    errorType: classifyDOMError(error.message || ""),
    url: typeof window !== "undefined" ? window.location.href : "",
    pathname: typeof window !== "undefined" ? window.location.pathname : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    timestamp: new Date().toISOString(),
    bodyChildCount: typeof document !== "undefined" ? document.body?.childNodes?.length || 0 : 0,
    headChildCount: typeof document !== "undefined" ? document.head?.childNodes?.length || 0 : 0,
    gtmLoaded: isScriptLoaded("gtm"),
    trustpilotLoaded: isScriptLoaded("trustpilot"),
    reactVersion: (globalThis as any).React?.version || "unknown",
    navigationTiming: getNavigationTiming(),
    errorStack: error.stack || "",
    errorMessage: error.message || "",
    suspectedSource,
    stackFrames,
  };

  return context;
}

/**
 * Reports a DOM manipulation error to Datadog with full context
 */
export function reportDOMErrorToDatadog(error: Error, source: string): void {
  const context = collectDOMErrorContext(error);

  console.error("Error:", error.message);
  console.log("Source:", source);
  console.log("Suspected cause:", context.suspectedSource);
  console.log("Stack frames:", context.stackFrames);
  console.log("Full context:", context);
  console.groupEnd();

  // Report to Datadog RUM
  try {
    datadogRum.addError(error, {
      source,
      ...context,
      stackFrame0: context.stackFrames[0] || "",
      stackFrame1: context.stackFrames[1] || "",
      stackFrame2: context.stackFrames[2] || "",
      stackFrame3: context.stackFrames[3] || "",
      stackFrame4: context.stackFrames[4] || "",
      scriptsLoaded: [
        context.gtmLoaded ? "GTM" : null,
        context.trustpilotLoaded ? "Trustpilot" : null,
      ]
        .filter(Boolean)
        .join(","),
    });

    datadogRum.addAction("dom_manipulation_error", {
      errorType: context.errorType,
      pathname: context.pathname,
      suspectedSource: context.suspectedSource,
      gtmLoaded: context.gtmLoaded,
    });
  } catch (e) {
    console.error("Failed to report error to Datadog:", e);
  }
}

/**
 * Checks if an error is a DOM manipulation error
 */
export function isDOMManipulationError(error: Error): boolean {
  const message = error.message || "";
  return (
    message.includes("removeChild") ||
    message.includes("insertBefore") ||
    message.includes("appendChild") ||
    message.includes("not a child of this node") ||
    message.includes("Failed to execute")
  );
}

/**
 * Checks if it's a cookie-related error
 */
export function isCookieError(error: Error): boolean {
  return (
    error.message?.includes("pending action cookie") ||
    error.message?.includes("Cookie-related error")
  );
}

/**
 * Reports a cookie error to Datadog with specific context
 */
export function reportCookieErrorToDatadog(error: Error, additionalData?: any): void {
  const context = {
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    timestamp: new Date().toISOString(),
    // Specific info for cookies
    cookieEnabled: typeof navigator !== "undefined" ? navigator.cookieEnabled : "unknown",
    storageLength: typeof localStorage !== "undefined" ? JSON.stringify(localStorage).length : 0,
    urlLength: typeof window !== "undefined" ? window.location.search.length : 0,
    ...additionalData,
  };

  try {
    datadogRum.addError(error, {
      source: "cookie-manager",
      category: "storage_error",
      ...context,
    });

    datadogRum.addAction("cookie_setting_failed", {
      urlLength: context.urlLength,
      pathname: typeof window !== "undefined" ? window.location.pathname : "",
    });
  } catch (e) {
    console.error("Failed to report cookie error to Datadog:", e);
  }
}

/**
 * Reports an error or warning to Datadog with optional context. Also logs with console.warn.
 * Use for non-critical failures (e.g. fallbacks) that should still be monitored.
 */
export function reportToDatadog(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  console.warn(err.message);
  try {
    datadogRum.addError(err, {
      source: "app",
      ...context,
    });
  } catch (e) {
    console.error("Failed to report to Datadog:", e);
  }
}
